import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "./api.ts";

export type ExamDetails = components["schemas"]["ExamDetails"];
export type ExamClassDetails = components["schemas"]["ExamClassDetails"];
export type ExamClassSheet = components["schemas"]["ExamClassSheet"];
export type ExamStudentMark = components["schemas"]["ExamStudentMark"];
export type SaveExamRequest = components["schemas"]["SaveExamRequest"];

/** Mirrors Exam.NameMaxLength on the server. */
export const EXAM_NAME_MAX_LENGTH = 200;

export const EXAMS_PAGE_SIZE = 25;

export const examKeys = {
  all: ["grading", "exams"] as const,
  list: (page: number) => ["grading", "exams", "list", page] as const,
  detail: (id: string) => ["grading", "exams", "detail", id] as const,
  sheet: (id: string, classId: string) => ["grading", "exams", "sheet", id, classId] as const,
  edits: (id: string, classId: string) => ["grading", "exams", "edits", id, classId] as const,
};

/** One page of exams, newest first; a teacher gets only the exams they grade. */
export function useExams(page: number) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: examKeys.list(page),
    queryFn: async () => {
      const { data, error } = await api.GET("/grading/exams", { params: { query: { Page: page, PageSize: EXAMS_PAGE_SIZE } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });
}

/** One exam with the classes the caller may read. */
export function useExam(examId: string) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: examKeys.detail(examId),
    queryFn: async () => {
      const { data, error } = await api.GET("/grading/exams/{examId}", { params: { path: { examId } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    enabled: examId !== "",
  });
}

/** Subject names for the planning form (academic.read). */
export function useSubjects(enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["academic", "subjects"],
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/subjects");
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });
}
